import React from "react";

/**
 * Candle Healthcare logo — burgundy shield + gold-outlined candle with a
 * two-line serif wordmark ("Candle / HEALTHCARE").  When `showTagline` is
 * true, the italic tagline "Nurturing Life's Light" appears as a full-width
 * row underneath the whole mark (matches the master brand artwork).
 */
export default function Logo({
  className = "h-9",
  onDark = false,
  showWordmark = true,
  showTagline = false,
  taglineClassName = "",
}) {
  const burgundy = "#7A1E38";
  const gold = "#C6A15B";
  const flame = "#E9B84A";
  const wordDark = "#5E1730";
  const wordLight = "#FBF7F0";
  const wm = onDark ? wordLight : wordDark;
  const sub = onDark ? "rgba(251,247,240,0.7)" : "#9A7B4F";
  const tagColor = onDark ? "#F5D08B" : "#B45309";

  return (
    <span className="inline-flex flex-col items-start">
      <span className="inline-flex items-center gap-2.5">
        <svg viewBox="0 0 48 52" className={`${className} w-auto`} role="img" aria-label="Candle Healthcare">
          <defs>
            <radialGradient id="cl-glow" cx="50%" cy="38%" r="45%">
              <stop offset="0%" stopColor={flame} stopOpacity="0.75" />
              <stop offset="100%" stopColor={flame} stopOpacity="0" />
            </radialGradient>
            <linearGradient id="cl-shield" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8A2440" />
              <stop offset="100%" stopColor="#5E1730" />
            </linearGradient>
          </defs>
          {/* Shield */}
          <path d="M24 2 L44 9 V25 C44 38 35 46 24 50 C13 46 4 38 4 25 V9 Z"
            fill="url(#cl-shield)" stroke={gold} strokeWidth="1.6" />
          <path d="M24 2 L44 9 V25 C44 38 35 46 24 50 C13 46 4 38 4 25 V9 Z"
            fill="none" stroke={gold} strokeOpacity="0.35" strokeWidth="0.6" transform="scale(0.9) translate(2.6 2.4)" />
          {/* Glow */}
          <circle cx="24" cy="20" r="13" fill="url(#cl-glow)" />
          {/* Candle body */}
          <rect x="20.5" y="24" width="7" height="15" rx="1.6" fill="#FBF3E4" />
          <rect x="20.5" y="24" width="2.4" height="15" rx="1.2" fill="#EAD9BC" />
          {/* Wick */}
          <rect x="23.4" y="20.5" width="1.2" height="4" rx="0.6" fill="#5E1730" />
          {/* Flame */}
          <path d="M24 8.5 C27.6 12.5 28.4 16.2 26.6 19 C25.7 20.4 24.9 21 24 21 C23.1 21 22.3 20.4 21.4 19 C19.6 16.2 20.4 12.5 24 8.5 Z"
            fill={flame} />
          <path d="M24 12 C26 14.4 26.4 16.6 25.3 18.4 C24.8 19.2 24.4 19.6 24 19.6 C23.6 19.6 23.2 19.2 22.7 18.4 C21.6 16.6 22 14.4 24 12 Z"
            fill="#FCE5B8" />
        </svg>
        {showWordmark && (
          <span className="font-serif-logo leading-none flex flex-col justify-center">
            <span style={{ color: wm }} className="text-[1.05rem] sm:text-xl font-bold tracking-tight">Candle</span>
            <span style={{ color: sub }} className="text-[0.6rem] sm:text-[0.68rem] font-medium tracking-[0.28em] uppercase mt-0.5">Healthcare</span>
          </span>
        )}
      </span>
      {showTagline && (
        <span
          data-testid="logo-tagline"
          style={{ color: tagColor }}
          className={`font-serif-logo italic tracking-wide mt-1.5 text-[0.72rem] sm:text-sm font-medium ${taglineClassName}`}
        >
          Nurturing Life&apos;s Light
        </span>
      )}
    </span>
  );
}
